/**
 * useSessionSync - Session synchronization hook for TRAC-147
 *
 * Checks session state on page load to ensure frontend is in sync with backend.
 * If the session_hash differs from what's stored locally, clears local messages.
 *
 * This handles the case where:
 * - Agent container restarted (15 min idle or 4 hour lifetime)
 * - User logged out and back in as different user
 * - Session expired (12 hour TTL)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendApiUrl } from '../config/backend';
import { logger } from '../utils/logger';
import { clearSessionSyncData } from '../utils/sessionSyncStorage';

// Storage keys for session sync (also defined in sessionSyncStorage.ts for the export)
const STORAGE_KEY_SESSION_HASH = 'traci_session_hash';
const STORAGE_KEY_SESSION_USER_ID = 'traci_session_user_id';

interface SessionStatus {
  user_id: string;
  session_hash: string | null;
  message_count: number;
  session_restored: boolean;
}

interface UseSessionSyncOptions {
  /** Function to get auth token for API calls */
  getToken: () => Promise<string | undefined>;
  /** Callback when session should be cleared */
  onSessionClear: () => void;
  /** Whether to skip sync (e.g., in embed mode without auth) */
  skip?: boolean;
}

interface UseSessionSyncResult {
  /** Whether sync is in progress */
  isLoading: boolean;
  /** Whether sync completed successfully */
  isSynced: boolean;
  /** Error message if sync failed */
  error: string | null;
  /** Whether session was cleared due to mismatch */
  wasCleared: boolean;
  /** Force a resync (e.g., after logout/login) */
  resync: () => Promise<void>;
}

/**
 * Get stored session data from localStorage
 */
function getStoredSessionData(): { userId: string | null; sessionHash: string | null } {
  try {
    const userId = localStorage.getItem(STORAGE_KEY_SESSION_USER_ID);
    const sessionHash = localStorage.getItem(STORAGE_KEY_SESSION_HASH);
    return { userId, sessionHash };
  } catch {
    return { userId: null, sessionHash: null };
  }
}

/**
 * Save session data to localStorage
 */
function saveSessionData(userId: string, sessionHash: string | null): void {
  try {
    if (userId) {
      localStorage.setItem(STORAGE_KEY_SESSION_USER_ID, userId);
    }
    if (sessionHash) {
      localStorage.setItem(STORAGE_KEY_SESSION_HASH, sessionHash);
    } else {
      localStorage.removeItem(STORAGE_KEY_SESSION_HASH);
    }
  } catch (e) {
    logger.warn('[SESSION_SYNC] Failed to save session data:', e);
  }
}

/**
 * Clear session data from localStorage.
 * Delegates to the extracted utility so both hook and service can use it.
 */
function clearSessionData(): void {
  clearSessionSyncData();
}

/**
 * Hook for synchronizing frontend session state with backend
 */
export function useSessionSync({
  getToken,
  onSessionClear,
  skip = false,
}: UseSessionSyncOptions): UseSessionSyncResult {
  const [isLoading, setIsLoading] = useState(!skip);
  const [isSynced, setIsSynced] = useState(skip);
  const [error, setError] = useState<string | null>(null);
  const [wasCleared, setWasCleared] = useState(false);
  const hasSyncedRef = useRef(false);

  const performSync = useCallback(async () => {
    if (skip) {
      setIsSynced(true);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get auth token
      const token = await getToken();
      if (!token) {
        logger.debug('[SESSION_SYNC] No auth token available, skipping sync');
        setIsSynced(true);
        setIsLoading(false);
        return;
      }

      // Fetch session status from backend
      const response = await fetch(`${getBackendApiUrl()}/api/session/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // 401/403 means not authenticated - don't treat as error
        if (response.status === 401 || response.status === 403) {
          logger.debug('[SESSION_SYNC] Not authenticated, skipping sync');
          setIsSynced(true);
          setIsLoading(false);
          return;
        }
        throw new Error(`Session status failed: ${response.status}`);
      }

      const serverStatus: SessionStatus = await response.json();
      const localData = getStoredSessionData();

      logger.debug('[SESSION_SYNC] Server status:', serverStatus);
      logger.debug('[SESSION_SYNC] Local data:', localData);

      // TRAC-203: Only clear when session is truly invalid, not on hash-only mismatch.
      // A hash change with a valid session just means the message count changed server-side
      // (e.g., the agent processed a response the frontend missed due to a stale SSE connection).
      // Wiping messages in that case makes the problem worse.
      const shouldClear =
        // User ID changed (different user logged in)
        (localData.userId && localData.userId !== serverStatus.user_id) ||
        // Server has no session but we have local data (session expired/deleted)
        (!serverStatus.session_restored && localData.sessionHash);

      if (shouldClear) {
        logger.debug('[SESSION_SYNC] Session mismatch detected, clearing local state');
        clearSessionData();
        onSessionClear();
        setWasCleared(true);
      } else {
        // TRAC-203: If session is valid but hash differs, just log it.
        // The hash is persisted below via saveSessionData() — no wipe needed.
        if (serverStatus.session_hash && localData.sessionHash &&
            localData.sessionHash !== serverStatus.session_hash) {
          logger.debug('[SESSION_SYNC] Hash mismatch with valid session — will update stored hash below');
        }
        setWasCleared(false);
      }

      // Save current session data for future comparisons
      saveSessionData(serverStatus.user_id, serverStatus.session_hash);

      setIsSynced(true);
    } catch (e) {
      logger.error('[SESSION_SYNC] Error syncing session:', e);
      setError(e instanceof Error ? e.message : 'Unknown error');
      // Don't block the UI on sync failure - just log it
      setIsSynced(true);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, onSessionClear, skip]);

  // Perform sync on mount
  useEffect(() => {
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      performSync();
    }
  }, [performSync]);

  return {
    isLoading,
    isSynced,
    error,
    wasCleared,
    resync: performSync,
  };
}

/**
 * Update stored session hash after receiving metrics from agent
 *
 * Call this when the agent returns a session_hash in the metrics event.
 * This keeps the local hash in sync with the server.
 */
export function updateSessionHash(sessionHash: string | null): void {
  if (sessionHash) {
    try {
      localStorage.setItem(STORAGE_KEY_SESSION_HASH, sessionHash);
      logger.debug('[SESSION_SYNC] Updated session hash:', sessionHash.substring(0, 8) + '...');
    } catch (e) {
      logger.warn('[SESSION_SYNC] Failed to update session hash:', e);
    }
  }
}

/**
 * Clear all session sync data (call on logout)
 */
export function clearSessionSyncData(): void {
  clearSessionData();
}

export default useSessionSync;
